<template>
  <div class="flex flex-col">
    <span class="text-sm border-b text-gray-400">Student Requests Control Panel</span>
    <div class="p-2 rounded-md bg-gray-100 shadow-sm">
      <div class="p-2 rounded-md bg-gray-50">
        <div class="p-2 flex flex-col gap-2">
          <div class="grid grid-cols-3 gap-2">
            <CalendarField
              v-model="formValues.startDate">
              Start Date
            </CalendarField>
            <CalendarField
              v-model="formValues.endDate">
              End Date
            </CalendarField>
          </div>
          <TextField class="w-1/2"
            v-model="formValues.filterStudents">
            Filter Students
          </TextField>
          <div class="flex flex-row">
            <MultiCheckboxField
              alignment="col"
              :options="checkboxOptions"
              v-model="formValues.selectedCheckboxOptions"
            />
          </div>
        </div>
        <div class="p-2 mb-2">
          <div class="text-gray-400">
            Status Key
            <div class="ml-4">
              <div>N = Not Applicable</div>
              <div>P = Pending</div>
              <div>R = Running</div>
              <div>E = Error</div>
              <div>C = Complete</div>
            </div>
          </div>
        </div>
        <div class="flex relative justify-center items-center mt-4">
          <PaginationButton
            ref="tableTop"
            :pages="pages"
            :initPage="currentPage"
            @toPage="onToPage"
          />
          <div class="absolute right-0 text-gray-400 mt-1 text-sm">
            Total Entries: {{ totalScheduleRequests }}
          </div>
        </div>
        <Spinner
          v-if="displayLoadingScheduleRequestsSpinner"
          message="Loading Schedule Requests"
          :size="16"
        />
        <div class="flex flex-col border-b-2 border-gray-400">
          <div class="flex flex-row bg-gray-200"
            @click="onToggleOpenTable">
            <AccordionIcon
              :open="openTable"
            />
            <div class="flex-grow"></div>
            <AccordionIcon
              :open="openTable"
            />
          </div>
        </div>
        <div v-show="openTable">
          <div class="border-solid border-b border-gray-600 mb-2">
            <div class="grid grid-cols-6 font-bold">
              <div class="col-span-2">Request Info</div>
              <div>Email Generated</div>
              <div>Noreply Email Generated</div>
              <div>Student Sync</div>
              <div>Building Sync</div>
            </div>
          </div>
          <div class="flex flex-col p-2 rounded-md bg-white gap-2">
            <div
              v-for="(request, index) in scheduleRequestEntries"
              :key="request.id">
              <ScheduleRequestControlPanel
                :index="index"
                :requestData="request"
                @runTask="runTask"
              />
            </div>
          </div>
          <div class="flex flex-col justify-center pb-4" v-if="openTable && scheduleRequestEntries.length > 0">
            <Button class="text-gray-300 text-sm w-full hover:bg-gray-100"
              @click="scrollToTableTop">
              Scroll To Top
            </Button>
            <div class="flex flex-row justify-center">
              <PaginationButton
                :pages="pages"
                :initPage="currentPage"
                @toPage="onToPage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import user from '../../../services/user/user.js'
import time from '../../../services/utils/time.js'
import scheduleRequestControlPanelService from '../../../services/activeSync/scheduleRequestControlPanel.js'

import CalendarField from '../../formComponents/CalendarField.vue'
import TextField from '../../formComponents/TextField.vue'

import Button from '../../Button.vue'
import AccordionIcon from '../../AccordionIcon.vue'
import PaginationButton from '../../PaginationButton.vue'

import ScheduleRequestControlPanel from './ScheduleRequestControlPanel.vue'
import MultiCheckboxField from '../../formComponents/MultiCheckboxField.vue'

export default {
  components: {
    CalendarField,
    TextField,
    Button,
    AccordionIcon,
    PaginationButton,
    MultiCheckboxField,
    ScheduleRequestControlPanel,
  },
  data() {
    return {
      openTable: false,
      formValues: {
        startDate: undefined,
        endDate: undefined,
        filterStudents: undefined,
        selectedCheckboxOptions: [],
      },

      displayLoadingScheduleRequestsSpinner: false,

      submissionTracking: {},
      confirmRerunTask: {},

      pages: undefined,
      totalScheduleRequests: 0,
      currentPage: 1,
      scheduleRequestEntries: [],
      showDetail: false,
      errorLogDatas: [],

      checkboxOptions: ['Show only incomplete', 'Show errors only'],
    }
  },
  methods: {
    callOnceWithDelay(callback, delayTime) {
      if (this.formValuesChangeApiCallDelay) {
        clearTimeout(this.formValuesChangeApiCallDelay)
      }
      this.formValuesChangeApiCallDelay = setTimeout(() => {
        callback()
      }, delayTime)
    },
    onToggleOpenTable() {
      this.openTable = !this.openTable
    },
    scrollToTableTop() {
      let ref = this.$refs.tableTop
      ref.$el.scrollIntoView({
        behavior: 'smooth',
      })
    },
    onToPage(pageNumber) {
      this.currentPage = pageNumber
      this.loadStudentRequests()
    },
    async loadStudentRequests() {
      let queryParams = this.createQueryParams()
      let serverData = await scheduleRequestControlPanelService.getData(queryParams)
      this.pages = serverData.pages
      this.totalScheduleRequests = serverData.page_data.num_schedule_requests
      this.scheduleRequestEntries = serverData.content.map(scheduleRequestControlPanelService.remapData)
    },
    createQueryParams() {
      let queryParams = {
        page: this.currentPage,
      }
      queryParams.createdAtStart = this.formValues.startDate
      queryParams.createdAtEnd = this.formValues.endDate
      queryParams.studentFilter = this.formValues.filterStudents
      if (this.formValues.selectedCheckboxOptions.includes('Show only incomplete')) {
        queryParams.onlyIncomplete = true
      }
      if (this.formValues.selectedCheckboxOptions.includes('Show errors only')) {
        queryParams.onlyError = true
      }
      return queryParams
    },
    displayStatus(requestValue) {
      return requestValue
    },
    rerunTaskButton(taskType) {
      console.log(taskType)
    },
    viewError(errorLogKey) {
      console.log(errorLogKey)
    },
    runTask(taskType, scheduleRequestId) {
      scheduleRequestControlPanelService.runTask(taskType, scheduleRequestId)
    },
  },
  async mounted() {
    await user.ready()
    this.formValues.startDate = time.today()
    this.formValues.selectedCheckboxOptions = ['Show errors only']
  },
  watch: {
    formValues: {
      deep: true,
      handler() {
        this.callOnceWithDelay(() => this.loadStudentRequests(), 1000)
      },
    }
  },
}
</script>
